import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Expand } from "lucide-react";
import { GALLERY } from "@/data/site";
import { fetchGallery, fetchHiddenBuiltins, fetchBuiltinOverrides, galleryImageUrl } from "@/lib/api";
import { SectionHeader, EASE } from "@/components/motion/Reveal";

const Lightbox = ({ items, index, onClose, onNav }) => {
  const item = items[index];
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", onKey);
    window.__lenis?.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      window.__lenis?.start();
    };
  }, [onClose, onNav]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-xl flex items-center justify-center p-4 sm:p-10"
      data-testid="gallery-lightbox"
    >
      <button onClick={onClose} className="absolute top-4 right-4 sm:top-6 sm:right-6 grid place-items-center w-11 h-11 rounded-full bg-white/10 hover:bg-brand hover:text-white text-white transition-colors" aria-label="Close" data-testid="gallery-lightbox-close">
        <X size={20} />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onNav(-1); }} className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 grid place-items-center w-11 h-11 rounded-full bg-white/10 hover:bg-brand hover:text-white text-white transition-colors" aria-label="Previous" data-testid="gallery-lightbox-prev">
        <ChevronLeft size={22} />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onNav(1); }} className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 grid place-items-center w-11 h-11 rounded-full bg-white/10 hover:bg-brand hover:text-white text-white transition-colors" aria-label="Next" data-testid="gallery-lightbox-next">
        <ChevronRight size={22} />
      </button>
      <AnimatePresence mode="wait">
        <motion.figure
          key={item.id}
          initial={{ opacity: 0, scale: 0.96, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.4, ease: EASE }}
          onClick={(e) => e.stopPropagation()}
          className="max-w-6xl w-full"
        >
          <img src={item.src} alt={item.title} className="w-full max-h-[75vh] object-contain rounded-lg shadow-2xl" data-testid="gallery-lightbox-img" />
          <figcaption className="mt-4 flex items-end justify-between gap-4">
            <div>
              <div className="font-display font-extrabold uppercase tracking-tight text-2xl text-white">{item.title}</div>
              <div className="text-sm text-slate-400">{item.sub}</div>
            </div>
            <div className="font-mono text-xs text-slate-500 tracking-widest">{String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}</div>
          </figcaption>
        </motion.figure>
      </AnimatePresence>
    </motion.div>
  );
};

const TILE = { span: "", aspect: "aspect-[4/3] lg:aspect-auto" };

export const useGalleryItems = () => {
  const [uploaded, setUploaded] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [overrides, setOverrides] = useState({});
  useEffect(() => {
    fetchGallery().then(setUploaded).catch(() => {});
    fetchHiddenBuiltins().then(setHidden).catch(() => {});
    fetchBuiltinOverrides().then(setOverrides).catch(() => {});
  }, []);
  return useMemo(() => {
    const builtins = GALLERY.filter((g) => !hidden.includes(g.id)).map((g) => ({ ...g, ...(overrides[g.id] || {}) }));
    const extra = uploaded.map((p) => ({ id: p.id, src: galleryImageUrl(p), title: p.title, sub: p.sub, ...TILE }));
    const items = [...builtins, ...extra];
    if (items.length) items[0] = { ...items[0], span: "lg:col-span-2 lg:row-span-2", aspect: "aspect-[16/10] lg:aspect-auto" };
    return items;
  }, [uploaded, hidden, overrides]);
};

export const Gallery = () => {
  const items = useGalleryItems();
  const [open, setOpen] = useState(null);
  const nav = (d) => setOpen((i) => (i + d + items.length) % items.length);
  if (items.length === 0) return null;

  return (
    <section id="gallery" data-testid="gallery-section" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeader
          id="gallery"
          eyebrow="On the tools"
          lines={["Real jobs.", <>Real <span className="text-brand-light">Bendigo.</span></>]}
          right="No stock photos here — this is Rickie, his rig and actual driveways around town. Tap any shot to see it up close."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:auto-rows-[270px]">
          {items.map((g, i) => (
            <motion.button
              key={g.id}
              type="button"
              onClick={() => setOpen(i)}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-8% 0px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: EASE }}
              className={`group relative overflow-hidden rounded-xl border border-white/10 hover:border-brand/60 bg-panel text-left ${g.span} ${g.aspect}`}
              data-testid={`gallery-item-${g.id}`}
            >
              <img src={g.src} alt={g.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 flex items-end justify-between gap-3">
                <div className="translate-y-1 group-hover:translate-y-0 transition-transform duration-300">
                  <div className="font-display font-extrabold uppercase tracking-tight text-lg sm:text-xl text-white leading-none">{g.title}</div>
                  <div className="text-xs text-slate-300/80 mt-1 line-clamp-1">{g.sub}</div>
                </div>
                <span className="shrink-0 grid place-items-center w-9 h-9 rounded-full bg-white/10 backdrop-blur text-white group-hover:bg-brand group-hover:text-white transition-colors duration-300">
                  <Expand size={15} />
                </span>
              </div>
              <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/70 bg-ink/60 backdrop-blur px-2 py-1 rounded">0{i + 1}</span>
            </motion.button>
          ))}
        </div>
      </div>
      <AnimatePresence>{open !== null && <Lightbox items={items} index={open} onClose={() => setOpen(null)} onNav={nav} />}</AnimatePresence>
    </section>
  );
};
